/**
 * Focus products: the specific products a search conversation is ABOUT.
 *
 * The conversation model names them in `focusProducts` ("iPad Pro", "iPad Air")
 * whenever the shopper names products instead of a category. The live shelf and
 * the comparison card then show THOSE products from our own catalogue, instead
 * of whatever pages happen to share the most words with the conversation — the
 * failure this exists for: "tell me the difference between ipad pro and air"
 * put Apple Pencils and stylus pages on the shelf, because accessory pages list
 * every model they fit and so cover the query's words better than either iPad.
 *
 * Deterministic and token-free: the model decides WHAT the shopper is talking
 * about, and this decides which catalogue products ARE those things.
 */

/** A comparison is readable side by side up to three; the shelf anchors as many. */
export const MAX_FOCUS_PRODUCTS = 3;

const MAX_FOCUS_NAME_LENGTH = 60;

/**
 * Normalise a model-written list into at most three distinct, short names.
 * Lenient on input shape (the field streams, and old transcripts lack it).
 */
export function sanitizeFocusProducts(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const name = entry.replace(/\s+/g, ' ').trim();
    if (name.length < 2 || name.length > MAX_FOCUS_NAME_LENGTH) continue;
    const key = normalizeProductName(name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(name);
    if (out.length === MAX_FOCUS_PRODUCTS) break;
  }
  return out;
}

/** Lowercase, accents folded, punctuation to spaces; "S26+" keeps its "plus". */
export function normalizeProductName(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/(\d)\+(?=\s|$|[),/])/g, '$1 plus ')
    .replace(/[™®©]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const tokensOf = (text: string): string[] => normalizeProductName(text).split(' ').filter(Boolean);

/** Plural folding for word comparisons ("pencils" is "pencil"; "glass" stays). */
const fold = (token: string): string =>
  token.length > 3 && token.endsWith('s') && !token.endsWith('ss') ? token.slice(0, -1) : token;

/**
 * Words that make a DIFFERENT model when the product adds them right after the
 * name and the focus name does not: "iPhone 17 Pro" is not the "iPhone 17 Pro
 * Max", "Galaxy S26" is not the "Galaxy S26 Ultra", "iPad" is not the "iPad Pro".
 */
const VARIANT_WORDS = new Set([
  'pro', 'max', 'plus', 'ultra', 'mini', 'lite', 'air', 'se', 'fe', 'xl', 'edge', 'slim',
  'fold', 'flip', 'neo', 'studio',
]);

/**
 * Words that make the product something you use WITH the named one: a stylus
 * for an iPad is not the iPad. Only unambiguous ones — "stand" (stand mixer),
 * "band", "lens" or "kit" name real products too often to veto on their own.
 * English and French, compared after plural folding.
 */
const ACCESSORY_WORDS = new Set([
  'case', 'cover', 'folio', 'sleeve', 'skin', 'bumper', 'stylus', 'pencil', 'pen', 'keyboard',
  'protector', 'film', 'charger', 'cable', 'adapter', 'dongle', 'dock', 'hub', 'mount', 'holder',
  'strap', 'remote', 'controller', 'replacement', 'refill', 'cartridge',
  'etui', 'housse', 'coque', 'stylet', 'clavier', 'protecteur', 'chargeur', 'adaptateur',
  'telecommande', 'manette', 'recharge',
]);

/** "for", "compatible with", "pour": a product sold FOR the thing named after it. */
const SOLD_FOR_WORDS = new Set(['for', 'fits', 'compatible', 'pour', 'compatibles']);

/** A leading brand or maker word may precede the name ("Apple iPad Pro", "Samsung Galaxy Tab S9"). */
const MAX_LEADING_TOKENS = 2;

/**
 * Tokens a title may add after the name without changing which product it is:
 * a size, a capacity, a generation or chip, a year, connectivity.
 */
const isSpecToken = (token: string): boolean =>
  /\d/.test(token) ||
  /^(?:inch|inches|in|pouces?|po|gb|go|tb|to|wifi|wi|fi|cellular|lte|gen|generation|generations|eme|e|th|nd|rd|st|chip|puce)$/.test(
    token,
  );

/** The first `count` words after `from` that are not spec tokens. */
const wordsAfter = (tokens: string[], from: number, count: number): string[] => {
  const out: string[] = [];
  for (let i = from; i < tokens.length && out.length < count; i++) {
    if (!isSpecToken(tokens[i])) out.push(tokens[i]);
  }
  return out;
};

/**
 * Does this catalogue product title NAME the focus product?
 *
 *   - Every word of the focus name appears, in order and adjacent, starting
 *     within the first `MAX_LEADING_TOKENS + 1` words of the title (a leading
 *     brand is fine: "Apple iPad Pro 11-inch").
 *   - Nothing before the name says it is sold for it ("Case for iPad Air",
 *     "Stylet iPad Pro") and the first words after it are not an accessory
 *     ("iPad Air Smart Folio", "iPad Pro Magic Keyboard").
 *   - The title adds no variant word right after the name ("Pro Max", "Ultra").
 *
 * The focus name may carry a brand the title leaves to its brand field
 * ("Apple iPad Pro" vs a title "iPad Pro 11-inch" with brand "Apple").
 */
export function productNamesFocus(
  focusName: string,
  productTitle: string | undefined | null,
  productBrand?: string | null,
): boolean {
  if (!productTitle || !focusName) return false;
  const titleTokens = tokensOf(productTitle);
  let nameTokens = tokensOf(focusName);
  if (nameTokens.length === 0 || titleTokens.length === 0) return false;

  // A brand the name carries but the title only has in its brand field.
  const brandTokens = productBrand ? tokensOf(productBrand) : [];
  if (
    brandTokens.length > 0 &&
    nameTokens.length > brandTokens.length &&
    brandTokens.every((t, i) => nameTokens[i] === t) &&
    !brandTokens.every((t, i) => titleTokens[i] === t)
  ) {
    nameTokens = nameTokens.slice(brandTokens.length);
  }

  const nameSet = new Set(nameTokens.map(fold));

  // The name as an ordered, adjacent run near the start of the title.
  let start = -1;
  let end = -1;
  for (let s = 0; s <= Math.min(MAX_LEADING_TOKENS, titleTokens.length - 1); s++) {
    let k = 0;
    while (
      k < nameTokens.length &&
      s + k < titleTokens.length &&
      fold(titleTokens[s + k]) === fold(nameTokens[k])
    ) {
      k += 1;
    }
    if (k === nameTokens.length) {
      start = s;
      end = s + k; // exclusive
      break;
    }
  }
  if (start < 0) return false;

  // Before the name: a brand is fine, a "for" or an accessory word is not.
  if (
    titleTokens
      .slice(0, start)
      .some((t) => SOLD_FOR_WORDS.has(t) || ACCESSORY_WORDS.has(fold(t)))
  ) {
    return false;
  }

  // Right after the name (spec tokens skipped): a variant word makes another
  // model, an accessory word or a "for" makes something used with it.
  const next = wordsAfter(titleTokens, end, 2);
  if (next[0] && VARIANT_WORDS.has(next[0]) && !nameSet.has(next[0])) return false;
  if (next.some((t) => SOLD_FOR_WORDS.has(t) || (ACCESSORY_WORDS.has(fold(t)) && !nameSet.has(fold(t))))) {
    return false;
  }

  return true;
}

/**
 * How closely a matching title names the focus product: the fewer extra
 * non-spec words, the better ("iPad Air 256GB" beats "iPad Air Wi-Fi Space
 * Grey Bundle"). Lower is better; only meaningful when productNamesFocus holds.
 */
export function focusMatchDistance(focusName: string, productTitle: string): number {
  const nameSet = new Set(tokensOf(focusName).map(fold));
  return tokensOf(productTitle).filter((t) => !nameSet.has(fold(t)) && !isSpecToken(t)).length;
}

export interface FocusCandidate<P> {
  /** Page (url_mappings) id the product was found on. */
  hitId: string;
  /** The product's rank on that page: 0 is the page's top pick. */
  position: number;
  /** The page's own rank among the candidate pages: 0 is the best page. */
  pageRank: number;
  product: P;
}

const compareKeys = (a: number[], b: number[]): number => {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] - b[i];
  return 0;
};

/**
 * For each focus name, the best catalogue product that names it, or null.
 * One product answers one name only, so "iPad Pro" and "iPad Pro 13" cannot
 * both claim the same card. Ties go to the closer title, then the better page,
 * then the product's own position on its page.
 */
export function pickFocusProducts<P extends { title: string; brand?: string | null }>(
  focusNames: string[],
  candidates: FocusCandidate<P>[],
): Array<FocusCandidate<P> | null> {
  // Keyed by title, not candidate: the same product can sit on two pages.
  const taken = new Set<string>();
  return focusNames.map((name) => {
    let best: FocusCandidate<P> | null = null;
    let bestKey: number[] | null = null;
    for (const candidate of candidates) {
      if (taken.has(normalizeProductName(candidate.product.title))) continue;
      if (!productNamesFocus(name, candidate.product.title, candidate.product.brand)) continue;
      const key = [focusMatchDistance(name, candidate.product.title), candidate.pageRank, candidate.position];
      if (!bestKey || compareKeys(key, bestKey) < 0) {
        best = candidate;
        bestKey = key;
      }
    }
    if (best) taken.add(normalizeProductName(best.product.title));
    return best;
  });
}
